import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchFacets } from '@app/src/api/fetchHooks'
import type {
  AssessmentLevelNamesType,
  AssessmentTemplate,
  AssessmentTemplateWithClassificationRules,
} from '@app/src/types/resourceExplorer'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { getTranslationKeysForLevels } from '@app/src/utils/assessmentLevelTranslations'
import { useMemo } from 'react'
import type { IntlFormatters } from 'react-intl'
import type { FacetItem } from '../../ResourceCollection'
import type { FilterGroup } from '../../ResourceCollection/Filters/useFilters'
import { LabelFormat } from '@app/src/utils/assessmentLevelTranslations'

export const getLevelTitle = (
  levelNumber: number,
  assessmentTemplate?: AssessmentTemplate,
  formatMessage?: IntlFormatters['formatMessage'],
): string => {
  if (!assessmentTemplate) return ''
  if (assessmentTemplate.assessmentType === AssessmentType.BaselineAssessment && formatMessage) {
    return formatMessage({ id: `baselineAssessment.levelDefinitions.level${levelNumber}Title` })
  }
  const levelTitleKey = `level${levelNumber}Title`
  return ((assessmentTemplate as unknown as Record<string, unknown>)?.[levelTitleKey] as string | undefined) || ''
}

export const getLevelDescription = (
  levelNumber: number,
  assessmentTemplate?: AssessmentTemplate,
  companyName?: string,
  formatMessage?: IntlFormatters['formatMessage'],
): string => {
  if (!assessmentTemplate) return ''
  if (assessmentTemplate.assessmentType === AssessmentType.BaselineAssessment && formatMessage) {
    return formatMessage(
      { id: `baselineAssessment.levelDefinitions.level${levelNumber}HelperText` },
      {
        companyName: companyName || formatMessage({ id: 'assessment.companyNameCopyPlaceholder' }),
      },
    )
  }
  const levelDescriptionKey = `level${levelNumber}Description`
  return ((assessmentTemplate as unknown as Record<string, unknown>)?.[levelDescriptionKey] as string | undefined) || ''
}

export const useGlobalMaxValue = (
  sections: AssessmentTemplateWithClassificationRules['sections'],
  graphFacetsFilters: FilterGroup[],
  levelsCount: number,
) => {
  const facetsFilters = useMemo(() => {
    return graphFacetsFilters.filter(group => !(group.name.startsWith('section') && group.name.endsWith('Level')))
  }, [graphFacetsFilters])

  const allSectionFacets = useFetchFacets({
    key: [FetchKey.AssessmentFacets, 'allSections'],
    endpoint: endpoints.assessmentFacet,
    facetsParam:
      sections?.map((_, index) => ({
        name: `section${index + 1}Level`,
        isEnum: true,
      })) || [],
    filter: facetsFilters,
  })

  const globalMaxValue = useMemo(() => {
    if (!sections || sections.length === 0) {
      return undefined
    }

    if (!allSectionFacets.facets || allSectionFacets.facets.length === 0) return undefined

    let maxValue = 0

    allSectionFacets.facets.forEach(sectionFacets => {
      if (Array.isArray(sectionFacets)) {
        const sectionLevels: Record<number, FacetItem | undefined> = {}
        for (let i = 1; i <= levelsCount; i++) {
          sectionLevels[i] = sectionFacets.find(l => l.value === i)
        }

        for (let i = 1; i <= levelsCount; i++) {
          const count = sectionLevels[i]?.count ?? 0
          if (count > maxValue) {
            maxValue = count
          }
        }
      }
    })

    return maxValue > 0 ? maxValue : undefined
  }, [allSectionFacets.facets, levelsCount, sections])

  return globalMaxValue
}

export const getLevelLabel = (
  levelValue: string | number,
  levelsCount: number,
  assessmentType: AssessmentType,
  levelNamesType: AssessmentLevelNamesType,
  formatMessage: IntlFormatters['formatMessage'],
  format: LabelFormat = LabelFormat.WithPercentage,
) => {
  const levelIndex = Number(levelValue) - 1
  const levelTranslations = getTranslationKeysForLevels(
    assessmentType,
    levelsCount,
    formatMessage,
    levelNamesType,
    format,
  )
  return levelTranslations[levelIndex] || formatMessage({ id: 'general.notAvailable' })
}

interface QuestionIdentifiable {
  id?: number
  questionId?: number
}

interface SectionWithQuestions {
  id: number
  questions: QuestionIdentifiable[]
}

/**
 * Builds a lookup from question ID → display number string for Flexible assessments.
 *
 * When the assessment has sections the numbers are hierarchical (e.g. "1.1", "1.2", "2.1").
 * When there are no sections the numbers are flat (e.g. "1.", "2.", "3.").
 */
function buildQuestionDisplayNumbers(
  sections: SectionWithQuestions[],
  questions: QuestionIdentifiable[],
): Map<number, string> {
  const map = new Map<number, string>()

  if (sections.length > 0) {
    sections.forEach((section, sectionIndex) => {
      section.questions.forEach((question, questionIndex) => {
        const id = question.questionId ?? question.id
        if (id) {
          map.set(id, `${sectionIndex + 1}.${questionIndex + 1}`)
        }
      })
    })
  } else {
    questions.forEach((question, index) => {
      const id = question.id ?? question.questionId
      if (id) {
        map.set(id, `${index + 1}.`)
      }
    })
  }

  return map
}

/**
 * Returns a map of question ID → display number string for Flexible assessments.
 * For non-Flexible assessments returns `undefined` (no display numbers are shown).
 *
 * Callers only need to supply the assessment type and the template data;
 * the hook decides internally whether to use sections or a flat list.
 */
export function useQuestionDisplayNumbers(
  assessmentType: AssessmentType,
  sections: SectionWithQuestions[],
  questions: QuestionIdentifiable[],
): Map<number, string> | undefined {
  return useMemo(() => {
    if (assessmentType !== AssessmentType.Flexible) {
      return undefined
    }
    return buildQuestionDisplayNumbers(sections, questions)
  }, [assessmentType, sections, questions])
}
